import Box from "@mui/material/Box";
import Checkbox from "@mui/material/Checkbox";
import MenuItem from "@mui/material/MenuItem";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";

export const ACCESS_LABEL = { read: "Read", write: "Write" };

export function accessText(row) {
  if (!row?.enabled) return "Not granted";
  return `${ACCESS_LABEL[row.access_type] || "Read"} / ${ACCESS_LABEL[row.service_access_type] || "Read"}`;
}

function AccessSelect({ value, onChange, disabled, label }) {
  return (
    <TextField
      select
      size="small"
      value={value || "read"}
      onChange={(event) => onChange(event.target.value)}
      disabled={disabled}
      inputProps={{ "aria-label": label }}
      sx={{ minWidth: 96 }}
    >
      <MenuItem value="read">Read</MenuItem>
      <MenuItem value="write">Write</MenuItem>
    </TextField>
  );
}

/**
 * rows: [{ key, name, contact_id?, type?, enabled, access_type, service_access_type, existing? }]
 * Device access is separate from receiving alerts or being a billing contact.
 */
export default function ContactAccessTable({ rows, onChange, readOnly = false }) {
  if (!rows?.length) {
    return (
      <Typography variant="body2" sx={{ opacity: 0.8 }}>
        No contacts are available for device access yet.
      </Typography>
    );
  }
  return (
    <Box sx={{ overflowX: "auto" }}>
      <Typography variant="caption" display="block" mb={1} sx={{ opacity: 0.8 }}>
        Device access lets a contact see and manage this device in Rivio. It is separate from receiving alerts and from
        being the billing contact. New grants default to Read / Read.
      </Typography>
      <Table size="small" aria-label="Contact device access">
        <TableHead sx={{ display: "table-header-group" }}>
          <TableRow>
            {!readOnly && <TableCell>Grant</TableCell>}
            <TableCell>Contact</TableCell>
            <TableCell>Contact ID</TableCell>
            <TableCell>Type</TableCell>
            <TableCell>Access Type</TableCell>
            <TableCell>Service Access Type</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row) => (
            <TableRow key={row.key}>
              {!readOnly && (
                <TableCell>
                  <Checkbox
                    checked={Boolean(row.enabled)}
                    onChange={(event) => onChange(row.key, { enabled: event.target.checked })}
                    inputProps={{ "aria-label": `Grant device access to ${row.name}` }}
                  />
                </TableCell>
              )}
              <TableCell>{row.name}</TableCell>
              <TableCell>{row.contact_id || "New"}</TableCell>
              <TableCell>{row.type || "-"}</TableCell>
              {readOnly || !row.enabled ? (
                <>
                  <TableCell>{row.enabled ? ACCESS_LABEL[row.access_type] || "Read" : "Not granted"}</TableCell>
                  <TableCell>{row.enabled ? ACCESS_LABEL[row.service_access_type] || "Read" : "Not granted"}</TableCell>
                </>
              ) : (
                <>
                  <TableCell>
                    <AccessSelect
                      value={row.access_type}
                      label={`Access type for ${row.name}`}
                      onChange={(value) => onChange(row.key, { access_type: value })}
                    />
                  </TableCell>
                  <TableCell>
                    <AccessSelect
                      value={row.service_access_type}
                      label={`Service access type for ${row.name}`}
                      onChange={(value) => onChange(row.key, { service_access_type: value })}
                    />
                  </TableCell>
                </>
              )}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Box>
  );
}
